/**
 * 修正依頼(`contentRequests`)— git-CMS(docs/10 §5 Phase 2)の前段。
 *
 * いまの運用: 管理画面で「訳がおかしい」「このテーマは違う」等をメモとして貯め、
 * **反映は人間(Claude)が `fixes.csv` 等に適用して content を再ビルドする**。
 * GitHub への直接コミットは次フェーズ。画面にもその旨を注記する。
 *
 * Firestore 側は admin のみ read/write(firebase/firestore.rules)。
 */
import {
  addDoc,
  collection,
  doc,
  getDocs,
  limit as fsLimit,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  type Firestore,
} from 'firebase/firestore';

export const CONTENT_REQUESTS_COLLECTION = 'contentRequests';

/** 修正メモの対象。`quiz` は まなび(5教科クイズ)の1問。 */
export const REQUEST_TARGET_TYPES = ['word', 'stage', 'quiz'] as const;
export type RequestTargetType = (typeof REQUEST_TARGET_TYPES)[number];
export type RequestStatus = 'open' | 'done';

export const REQUEST_TARGET_TYPE_LABELS: Readonly<Record<RequestTargetType, string>> = {
  word: '単語',
  stage: 'ステージ',
  quiz: 'まなび問題',
};

/** 修正の種別。ラベルは画面のセレクトにそのまま出す。 */
export const REQUEST_KINDS = [
  'meaning',
  'reading',
  'theme',
  'ng',
  'media',
  'stage-composition',
  'quiz-prompt',
  'quiz-choices',
  'quiz-answer',
  'quiz-explanation',
  'quiz-skills',
  'quiz-unit',
  'other',
] as const;
export type RequestKind = (typeof REQUEST_KINDS)[number];

export const REQUEST_KIND_LABELS: Readonly<Record<RequestKind, string>> = {
  meaning: '訳の修正',
  reading: '読み・表記の修正',
  theme: 'テーマ(タグ)の変更',
  ng: 'NG(この語を外したい)',
  media: '画像・音声の差し替え',
  'stage-composition': 'ステージ構成の変更',
  'quiz-prompt': '問題文・ふりがなの修正',
  'quiz-choices': '選択肢の修正',
  'quiz-answer': '正解が違う',
  'quiz-explanation': '解説の修正',
  'quiz-skills': 'skills タグの変更',
  'quiz-unit': '単元の割り当て変更',
  other: 'その他',
};

/**
 * 対象ごとに出す種別。
 *
 * 全種別を1つのセレクトに並べると「まなびの問題に『訳の修正』」のような
 * 意味の無い組み合わせが選べてしまうので、対象で絞る。
 * Firestore ルールは `kind is string` しか見ないので、増やすのはルール変更不要。
 */
export const REQUEST_KINDS_BY_TARGET: Readonly<Record<RequestTargetType, readonly RequestKind[]>> =
  {
    word: ['meaning', 'reading', 'theme', 'ng', 'media', 'other'],
    stage: ['stage-composition', 'ng', 'media', 'other'],
    quiz: [
      'quiz-prompt',
      'quiz-choices',
      'quiz-answer',
      'quiz-explanation',
      'quiz-skills',
      'quiz-unit',
      'media',
      'ng',
      'other',
    ],
  };

export const REQUEST_STATUS_LABELS: Readonly<Record<RequestStatus, string>> = {
  open: '未対応',
  done: '対応済み',
};

/** 本文の上限。ルール側の検証と揃える。 */
export const MAX_BODY_LENGTH = 2000;
/** 対象表示名の上限。ルール側(targetLabel.size() <= 256)と揃える。 */
export const MAX_LABEL_LENGTH = 256;

export interface ContentRequest {
  readonly id: string;
  readonly uid: string;
  readonly targetType: RequestTargetType;
  readonly targetId: string;
  /** 一覧で対象が分かるようにした表示名(単語の display / ステージ名 / 問題文)。 */
  readonly targetLabel: string;
  readonly language: string;
  /** まなびの帯(`kinder` / `e1` …)。単語・ステージでは空。 */
  readonly band: string;
  readonly kind: RequestKind;
  readonly body: string;
  readonly status: RequestStatus;
  readonly createdAt: Date | null;
}

export interface NewContentRequest {
  readonly targetType: RequestTargetType;
  readonly targetId: string;
  readonly targetLabel: string;
  readonly language: string;
  /** まなびの帯。省略可(単語・ステージは付けない)。 */
  readonly band?: string;
  readonly kind: RequestKind;
  readonly body: string;
}

export class RequestError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'RequestError';
  }
}

/** 送信前の検証。空メモや長すぎる本文を Firestore まで飛ばさない。 */
export function validateNewRequest(input: NewContentRequest): string | null {
  if (input.body.trim() === '') return '修正メモの本文を入力してください。';
  if (input.body.length > MAX_BODY_LENGTH) {
    return `本文が長すぎます(${String(MAX_BODY_LENGTH)}文字まで)。`;
  }
  if (input.targetId.trim() === '') return '対象が指定されていません。';
  return null;
}

function toDate(value: unknown): Date | null {
  if (value && typeof value === 'object' && 'toDate' in value) {
    const toDateFn = (value as { toDate: unknown }).toDate;
    if (typeof toDateFn === 'function') return (toDateFn as () => Date).call(value);
  }
  return null;
}

/** Firestore のドキュメントを画面用の形へ。壊れた値は既定値で埋める。 */
export function toContentRequest(id: string, data: Record<string, unknown>): ContentRequest {
  const kind = data.kind;
  const status = data.status;
  const targetType = data.targetType;
  return {
    id,
    uid: typeof data.uid === 'string' ? data.uid : '',
    targetType: (REQUEST_TARGET_TYPES as readonly unknown[]).includes(targetType)
      ? (targetType as RequestTargetType)
      : 'word',
    targetId: typeof data.targetId === 'string' ? data.targetId : '',
    targetLabel: typeof data.targetLabel === 'string' ? data.targetLabel : '',
    language: typeof data.language === 'string' ? data.language : '',
    band: typeof data.band === 'string' ? data.band : '',
    kind: (REQUEST_KINDS as readonly string[]).includes(kind as string)
      ? (kind as RequestKind)
      : 'other',
    body: typeof data.body === 'string' ? data.body : '',
    status: status === 'done' ? 'done' : 'open',
    createdAt: toDate(data.createdAt),
  };
}

export class ContentRequestService {
  constructor(
    private readonly db: Firestore,
    private readonly uid: string,
  ) {}

  async create(input: NewContentRequest): Promise<void> {
    const problem = validateNewRequest(input);
    if (problem) throw new RequestError(problem);
    const band = input.band?.trim() ?? '';
    await addDoc(collection(this.db, CONTENT_REQUESTS_COLLECTION), {
      uid: this.uid,
      targetType: input.targetType,
      targetId: input.targetId,
      // ルール側の上限(256)を超えると書込ごと弾かれる。問題文をそのまま
      // ラベルにする まなび では普通に起きうるので、ここで切っておく。
      targetLabel: input.targetLabel.slice(0, MAX_LABEL_LENGTH),
      language: input.language,
      ...(band === '' ? {} : { band }),
      kind: input.kind,
      body: input.body.trim(),
      status: 'open' satisfies RequestStatus,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  }

  /**
   * 新しい順に取得する。
   *
   * 未対応/対応済みの切替は**クライアント側で絞る** — where + orderBy の複合
   * インデックスを増やしたくないのと、件数が管理者の手作業ぶん(数百)しか
   * 出ないため。件数が増えたら status 付きインデックスを張って移す。
   */
  async list(max = 200): Promise<readonly ContentRequest[]> {
    const snapshot = await getDocs(
      query(
        collection(this.db, CONTENT_REQUESTS_COLLECTION),
        orderBy('createdAt', 'desc'),
        fsLimit(max),
      ),
    );
    return snapshot.docs.map((document) =>
      toContentRequest(document.id, document.data() as Record<string, unknown>),
    );
  }

  async setStatus(id: string, status: RequestStatus): Promise<void> {
    await updateDoc(doc(this.db, CONTENT_REQUESTS_COLLECTION, id), {
      status,
      updatedAt: serverTimestamp(),
    });
  }
}
